import React, { useEffect, useState } from 'react';
import { fieldProps } from './missingFields';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import FileUpload from '../../../components/ui/FileUpload';
import Icon from '../../../components/AppIcon';
import { validateFileUpload, gstIndicatesTransport } from './FormValidation';
import { StepHeader, StepNote } from './StepHeader';
import LockedField from './LockedField';
import DirectorsList from './DirectorsList';
import { useLocked } from '../hooks/useLocked';
import { isIndia } from '../../../utils/countries';
import { registrationService, getErrorMessage } from '../../../services/apiService';
import DocumentReadPanel from './DocumentReadPanel';
import FieldFlag from './FieldFlag';

const ComplianceStep = ({
  formData,
  updateFormData,
  errors,
  flaggedFields,
  onPreviewDocument,
  keepExistingDocuments,
  setKeepExistingDocuments,
  uploadedFiles,
  locked,
  lockSources,
  identity,
  sessionId,
  kycActive = false,
  kyc,
  onDocumentWarnings,
}) => {
  const lock = useLocked(locked, lockSources, 5);
  const registryVerified = Boolean(identity?.verified_at);
  const ocrOn = kycActive && kyc?.enabled !== false;
  // GTA declaration is transport-only (item 7): show it only when the GST registry marks the
  // vendor as a transporter. Hidden entirely otherwise.
  const gtaApplicable = gstIndicatesTransport(identity);

  // Both compliance documents are read on upload and offered to the form — the PAN card fills
  // the PAN, the GST certificate fills the GSTIN, the legal and trade names, the constitution
  // and the registered address. `readable` is the backend's own view of whether it can read.
  const readable = Boolean(sessionId) && kyc?.enabled !== false;
  const [docFile, setDocFile] = useState({}); // { PAN_CARD: File, GST_CERTIFICATE: File }

  // A document already on file was read when it was uploaded; on a resumed form its warnings —
  // and any block — come back from the server, so refreshing the page never gets past a wrong document.
  useEffect(() => {
    if (!readable || !onDocumentWarnings) return;
    for (const kind of ['PAN_CARD', 'GST_CERTIFICATE']) {
      if (!uploadedFiles?.[kind]) continue;
      registrationService
        .lastDocumentRead(sessionId, kind)
        .then((res) => onDocumentWarnings(kind, res?.data?.warnings || []))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readable, sessionId]);

  const handleDocUpload = (docType, field, file) => {
    handleFileUpload(field, file);
    setDocFile((p) => ({ ...p, [docType]: file || null }));
  };

  /** What the document filled in goes straight into the form the vendor is looking at. */
  const onRead = (applied) => updateFormData(Object.fromEntries(applied.map((a) => [a.key, a.value])));

  /** The "we read your document" panel, under whichever upload produced the file. */
  const renderDocRead = (docType) =>
    readable && docFile[docType] ? (
      <DocumentReadPanel
        sessionId={sessionId}
        kind={docType}
        file={docFile[docType]}
        onApplied={onRead}
        onWarnings={(warnings) => onDocumentWarnings?.(docType, warnings)}
        confirmedBy={formData.email || formData.directorEmail || ''}
        onCleared={() => setDocFile((p) => ({ ...p, [docType]: null }))}
        className="mt-3"
      />
    ) : null;
  const showCinInput =
    !lock.isLocked('cinNumber') && identity?.mca_status === 'failed' && ['company', 'llp'].includes(identity?.entity_kind);
  const [currencySearch, setCurrencySearch] = useState('');

  const currencies = [
    { value: 'INR', label: 'INR - Indian Rupee' },
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'JPY', label: 'JPY - Japanese Yen' },
    { value: 'AUD', label: 'AUD - Australian Dollar' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
    { value: 'CHF', label: 'CHF - Swiss Franc' },
    { value: 'CNY', label: 'CNY - Chinese Yuan' },
    { value: 'SGD', label: 'SGD - Singapore Dollar' },
    { value: 'AED', label: 'AED - UAE Dirham' },
    { value: 'SAR', label: 'SAR - Saudi Riyal' }
  ];

  const natureOfAssessee = [
    { value: 'individual', label: 'Individual' },
    { value: 'huf', label: 'Hindu Undivided Family (HUF)' },
    { value: 'company', label: 'Company' },
    { value: 'firm', label: 'Firm/LLP' },
    { value: 'aop', label: 'Association of Persons (AOP)' },
    { value: 'body-of-individuals', label: 'Body of Individuals (BOI)' },
    { value: 'local-authority', label: 'Local Authority' },
    { value: 'artificial-juridical-person', label: 'Artificial Juridical Person' },
    { value: 'trust', label: 'Trust' },
    { value: 'society', label: 'Society' }
  ];

  const handleInputChange = (field, value) => {
    // Auto-uppercase PAN
    if (field === 'panNumber') {
      value = value.toUpperCase();
    }
    updateFormData({ [field]: value });
  };

  const handleFileUpload = (field, file) => {
    // FileUpload component now handles validation internally
    updateFormData({ [field]: file });
  };

  const handleRemoveFile = (field) => {
    updateFormData({ [field]: null });
  };

  const isIndian = isIndia(formData.countryOrigin) || formData.registeredCountry === 'IN';

  return (
    <div className="space-y-6">
      <StepHeader
        title="Compliance Information"
        description={registryVerified ? 'Your tax identity is verified against GSTN and MCA. Upload the supporting documents and answer the one question no registry holds.' : 'Tax and regulatory details required for your country.'}
      >
        {registryVerified && (
          <StepNote tone="success" icon="ShieldCheck">
            PAN, GSTIN, constitution and nature of assessee were verified live in the Identity step and cannot be edited here.
          </StepNote>
        )}
      </StepHeader>

      {/* Preferred Currency moved to Categorization step */}

      {/* India-specific fields */}
      {isIndian && (
        <div className="bg-muted/30 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">India-specific Compliance</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              {lock.isLocked('panNumber') ? (
                <LockedField label="PAN Number" value={formData.panNumber} source={lock.sourceOf('panNumber')} required description="Derived from your GSTIN and confirmed by GSTN" />
              ) : (
                <Input
                  label="PAN Number"
                  type="text"
                  placeholder="Enter PAN number"
                  value={formData.panNumber}
                  {...fieldProps('panNumber')}
                  onChange={(e) => handleInputChange('panNumber', e.target.value)}
                  error={errors.panNumber}
                  maxLength={10}
                  required
                />
              )}
              {/* Show checkbox if document is pre-filled */}
              {uploadedFiles?.PAN_CARD && !formData.panDocument?.name && (
                <div className="mb-2 p-3 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="keepPanDocument"
                        checked={keepExistingDocuments.panDocument || false}
                        onChange={(e) => setKeepExistingDocuments(prev => ({ ...prev, panDocument: e.target.checked }))}
                        className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                      />
                      <label htmlFor="keepPanDocument" className="text-sm text-foreground cursor-pointer">
                        Keep existing: <span className="font-medium">{uploadedFiles.PAN_CARD.file_name}</span>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => onPreviewDocument && onPreviewDocument(uploadedFiles.PAN_CARD, 'PAN_CARD')}
                      className="px-3 py-1 text-sm text-primary hover:text-primary/80 hover:bg-primary/5 rounded-md transition-colors flex items-center space-x-1"
                    >
                      <Icon name="eye" className="w-4 h-4" />
                      <span>Preview</span>
                    </button>
                  </div>
                </div>
              )}
              {(!keepExistingDocuments.panDocument || !uploadedFiles?.PAN_CARD) && (
                <FileUpload
                  label="Upload PAN Document"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={2 * 1024 * 1024}
                  {...fieldProps('panDocument')}
                  value={formData.panDocument}
                  onChange={(file) => handleDocUpload('PAN_CARD', 'panDocument', file)}
                  onRemove={() => { handleRemoveFile('panDocument'); setDocFile((p) => ({ ...p, PAN_CARD: null })); }}
                  onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'PAN_CARD')}
                  error={errors.panDocument}
                  required
                  description="Max 2MB - PDF, JPG, or PNG format"
                />
              )}
              {renderDocRead('PAN_CARD')}
            </div>

            <div className="space-y-2">
              {lock.isLocked('gstNumber') ? (
                <LockedField label="GST Number" value={formData.gstNumber} source={lock.sourceOf('gstNumber')} required description={formData.gstStatus ? `Verified with GSTN · status ${formData.gstStatus}` : undefined} />
              ) : (
                <Input
                  label="GST Number"
                  type="text"
                  placeholder="Enter GST number"
                  value={formData.gstNumber}
                  {...fieldProps('gstNumber')}
                  onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                  error={errors.gstNumber}
                  maxLength={15}
                  required
                />
              )}
              <FieldFlag flaggedFields={flaggedFields} name="gstNumber" />
              {/* Show checkbox if document is pre-filled */}
              {uploadedFiles?.GST_CERTIFICATE && !formData.gstDocument?.name && (
                <div className="mb-2 p-3 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="keepGstDocument"
                        checked={keepExistingDocuments.gstDocument || false}
                        onChange={(e) => setKeepExistingDocuments(prev => ({ ...prev, gstDocument: e.target.checked }))}
                        className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                      />
                      <label htmlFor="keepGstDocument" className="text-sm text-foreground cursor-pointer">
                        Keep existing: <span className="font-medium">{uploadedFiles.GST_CERTIFICATE.file_name}</span>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => onPreviewDocument && onPreviewDocument(uploadedFiles.GST_CERTIFICATE, 'GST_CERTIFICATE')}
                      className="px-3 py-1 text-sm text-primary hover:text-primary/80 hover:bg-primary/5 rounded-md transition-colors flex items-center space-x-1"
                    >
                      <Icon name="eye" className="w-4 h-4" />
                      <span>Preview</span>
                    </button>
                  </div>
                </div>
              )}
              {(!keepExistingDocuments.gstDocument || !uploadedFiles?.GST_CERTIFICATE) && (
                <FileUpload
                  label="Upload GST Document"
                  accept=".pdf,.jpg,.jpeg,.png"
                  maxSize={3 * 1024 * 1024}
                  {...fieldProps('gstDocument')}
                  value={formData.gstDocument}
                  onChange={(file) => handleDocUpload('GST_CERTIFICATE', 'gstDocument', file)}
                  onRemove={() => { handleRemoveFile('gstDocument'); setDocFile((p) => ({ ...p, GST_CERTIFICATE: null })); }}
                  onPreview={(file) => onPreviewDocument && onPreviewDocument(file, 'GST_CERTIFICATE')}
                  error={errors.gstDocument}
                  required
                  description="Max 3MB - PDF, JPG, or PNG format"
                />
              )}
              {renderDocRead('GST_CERTIFICATE')}
            </div>

            {lock.isLocked('natureOfAssessee') ? (
              <LockedField
                label="Nature of Assessee"
                displayValue={(natureOfAssessee.find((o) => o.value === formData.natureOfAssessee) || {}).label || formData.natureOfAssessee}
                source={lock.sourceOf('natureOfAssessee')}
                required
                description="Derived from the 4th character of your PAN"
              />
            ) : (
              <Select
                label="Nature of Assessee"
                description="Select your tax assessee category"
                options={natureOfAssessee}
                value={formData.natureOfAssessee}
                onChange={(value) => handleInputChange('natureOfAssessee', value)}
                error={errors.natureOfAssessee}
                required
              />
            )}

            {lock.isLocked('constitutionOfBusiness') && (
              <LockedField label="Constitution of Business" value={formData.constitutionOfBusiness} source={lock.sourceOf('constitutionOfBusiness')} />
            )}
            {lock.isLocked('gstRegistrationDate') && (
              <LockedField label="GST Registration Date" value={formData.gstRegistrationDate} source={lock.sourceOf('gstRegistrationDate')} />
            )}
            {lock.isLocked('cinNumber') && (
              <LockedField label="CIN" value={formData.cinNumber} source={lock.sourceOf('cinNumber')} />
            )}
            {showCinInput && (
              <Input
                label="CIN"
                type="text"
                placeholder="Enter CIN (optional)"
                value={formData.cinNumber || ''}
                onChange={(e) => handleInputChange('cinNumber', e.target.value.toUpperCase())}
                error={errors.cinNumber}
                maxLength={21}
                description="We couldn't reach MCA — add your CIN if you have one"
              />
            )}
            <DirectorsList directors={identity?.mca?.directors || []} cin={identity?.mca?.cin} />

            {gtaApplicable && (
              <Select
                label="GTA Registration"
                description="Are you registered under Goods Transport Agency (GTA)? Shown because your GST nature-of-business indicates transportation."
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' }
                ]}
                value={formData.gtaRegistration}
                {...fieldProps('gtaRegistration')}
                onChange={(value) => handleInputChange('gtaRegistration', value)}
                error={errors.gtaRegistration}
                required
              />
            )}

            {/* Place of Supply removed as per requirement */}
          </div>
        </div>
      )}

      {/* International compliance */}
      {!isIndian && (
        <div className="bg-muted/30 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-foreground mb-4">International Compliance</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="VAT/Sales Tax Number"
              type="text"
              placeholder="Enter VAT or sales tax number"
              value={formData.vatNumber}
              onChange={(e) => handleInputChange('vatNumber', e.target.value)}
              error={errors.vatNumber}
            />

            <Input
              label="Business License Number"
              type="text"
              placeholder="Enter business license number"
              value={formData.businessLicense}
              onChange={(e) => handleInputChange('businessLicense', e.target.value)}
              error={errors.businessLicense}
              required
            />

            {gtaApplicable && (
              <Select
                label="GTA Registration"
                description="Are you registered under Goods Transport Agency (GTA)? Shown because your GST nature-of-business indicates transportation."
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' }
                ]}
                value={formData.gtaRegistration}
                {...fieldProps('gtaRegistration')}
                onChange={(value) => handleInputChange('gtaRegistration', value)}
                error={errors.gtaRegistration}
                required
              />
            )}

            <div className="md:col-span-2">
              <Input
                label="Regulatory Compliance Notes"
                type="text"
                placeholder="Any additional regulatory compliance information"
                value={formData.complianceNotes}
                onChange={(e) => handleInputChange('complianceNotes', e.target.value)}
                error={errors.complianceNotes}
              />
            </div>
          </div>
        </div>
      )}

      {/* Additional Information section removed as per requirement */}

      {/* Information Notice */}
      <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-5 h-5 bg-warning rounded-full flex items-center justify-center mt-0.5">
            <span className="text-white text-xs font-bold">!</span>
          </div>
          <div>
            <h4 className="font-medium text-foreground mb-1">Compliance Verification</h4>
            <p className="text-sm text-text-secondary">
              All compliance information will be verified during the approval process. 
              Please ensure all details are accurate and up-to-date. Supporting documents 
              may be requested for verification.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceStep;